import React, { useState, useCallback } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import {
  EditOutlined,
  SettingsOutlined,
  Inventory2Outlined,
  ViewModuleOutlined,
  EditNoteOutlined,
  ChevronRight,
} from "@mui/icons-material";
import FamilyManagement from "./menus/FamilyManagementProduct/FamilyManagement";
import { InventoryCombinationsDisplay } from "./menus/ProductInventoryCombinations/InventoryCombinationsDisplay";
import { CreateProductWizard } from "../../../../components/forms/CreateProductWizard/CreateProductWizard";

interface ListingContextMenuProps {
  open: boolean;
  event: MouseEvent | null;
  closeContextMenu: () => void;
  selectedRows: any[];
  onGridRefresh?: () => void;
}

const ListingContextMenu: React.FC<ListingContextMenuProps> = ({
  open,
  event,
  closeContextMenu,
  selectedRows,
  onGridRefresh,
}) => {
  const [inventoryAnchorEl, setInventoryAnchorEl] =
    useState<null | HTMLElement>(null);
  const [activeModal, setActiveModal] = useState<string>("");
  const [productWizardConfig, setProductWizardConfig] = useState<{
    initialProduct: any;
    mode: "edit" | "create";
    initialPage: number;
  } | null>(null);

  const openContextModal = useCallback((modalName: string) => {
    setActiveModal(modalName);
    setInventoryAnchorEl(null);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal("");
    setProductWizardConfig(null);
  }, []);

  const closeModalAndRefresh = useCallback(() => {
    setActiveModal("");
    setProductWizardConfig(null);
    if (onGridRefresh) {
      onGridRefresh();
    }
    closeContextMenu();
  }, [onGridRefresh]);

  const handleOpenEditPartNumber = useCallback(() => {
    if (selectedRows.length === 0) return;

    const row = selectedRows[0];
    setProductWizardConfig({
      initialProduct: row,
      mode: "edit",
      initialPage: 0,
    });
    setActiveModal("productWizard");
    setInventoryAnchorEl(null);
    closeContextMenu();
  }, [selectedRows, closeContextMenu]);

  const handleOpenEditInventory = useCallback(() => {
    if (selectedRows.length === 0) return;

    const row = selectedRows[0];
    setProductWizardConfig({
      initialProduct: row,
      mode: "edit",
      initialPage: 2, // Start on inventory page
    });
    setActiveModal("productWizard");
    setInventoryAnchorEl(null);
    closeContextMenu();
  }, [selectedRows, closeContextMenu]);

  const handleInventoryMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setInventoryAnchorEl(event.currentTarget);
  };

  const handleInventoryMenuClose = () => {
    setInventoryAnchorEl(null);
  };

  const handleMainMenuClose = () => {
    setInventoryAnchorEl(null);
    setActiveModal("");
    setProductWizardConfig(null);
    closeContextMenu();
  };

  if (!open) return null;

  // If there's an active modal, only render the modal
  if (activeModal) {
    switch (activeModal) {
      case "family":
        return (
          <FamilyManagement
            open={true}
            close={closeModalAndRefresh}
            initialProducts={selectedRows}
          />
        );
      case "inventory-combinations":
        if (selectedRows.length === 1) {
          return (
            <InventoryCombinationsDisplay
              open={true}
              close={closeModal}
              product={selectedRows[0]}
            />
          );
        }
        break;
      case "productWizard":
        if (productWizardConfig) {
          return (
            <CreateProductWizard
              mode={productWizardConfig.mode}
              initialProduct={productWizardConfig.initialProduct}
              initialPage={productWizardConfig.initialPage}
              onClose={closeModalAndRefresh}
            />
          );
        }
        break;
    }
  }

  const menuPosition = event
    ? {
        top: event.pageY - window.scrollY,
        left: event.pageX,
      }
    : { top: 0, left: 0 };

  return (
    <>
      <Menu
        open={open}
        onClose={handleMainMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
      >
        {selectedRows.length > 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              ({selectedRows.length}) selected
            </Typography>
          </MenuItem>
        )}
        {selectedRows.length > 0 && <Divider />}

        <MenuItem onClick={() => openContextModal("family")}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Families</ListItemText>
        </MenuItem>

        <MenuItem
          disabled={selectedRows.length > 1}
          onClick={handleOpenEditPartNumber}
        >
          <ListItemIcon>
            <SettingsOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Part Number</ListItemText>
        </MenuItem>

        <MenuItem onMouseEnter={handleInventoryMenuOpen}>
          <ListItemIcon>
            <Inventory2Outlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Inventory</ListItemText>
          <ChevronRight fontSize="small" />
        </MenuItem>
      </Menu>

      {/* Inventory Submenu */}
      <Menu
        open={Boolean(inventoryAnchorEl)}
        anchorEl={inventoryAnchorEl}
        onClose={handleInventoryMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          root: {
            onMouseLeave: handleInventoryMenuClose,
          },
        }}
      >
        <MenuItem
          disabled={selectedRows.length > 1}
          onClick={() => openContextModal("inventory-combinations")}
        >
          <ListItemIcon>
            <ViewModuleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Sets</ListItemText>
        </MenuItem>

        <MenuItem
          disabled={selectedRows.length > 1}
          onClick={handleOpenEditInventory}
        >
          <ListItemIcon>
            <EditNoteOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Listing</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ListingContextMenu;
